import React, { ChangeEvent, useCallback } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import { OptionBase } from '../../../../../../../components/Select/types';
import { ProjectWithStatus } from '../../../types';
import { TimeEntryData } from './DayTimeEntriesForm';
import { Theme } from '../../../../../../../theme';
import styled from '@emotion/styled';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import { minToHoursInput } from '../../helpers';
import {
  CostCenter,
  ExtraFieldInput,
  FieldType,
} from '../../../../../__generated__/graphql';
import {
  hoursToMinutes,
  minutesToHoursAndMinutes,
} from '../../../../../../../utils/time';
import {
  ExtraFieldsAreaSimple,
  ExtraFieldsSupportedObject,
} from '../../../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../../../components/ExtraFields/types';
import { useUserInfo } from '../../../../../context/UserInfo';

type Props = {
  day: string;
  timeTracked: TimeEntryData;
  projects: ProjectWithStatus[];
  costCenters: ReadonlyArray<CostCenter>;
  disabled: boolean;
  onDataChange: (timeTracked: TimeEntryData) => void;
  onDelete: (id: string) => void;
};

const StyledSelect = styled(Form.Select)((props) =>
  props.disabled
    ? {
        backgroundColor: `${Theme.color.white} !important`,
      }
    : {},
);

const DayTimeEntry: React.FC<Props> = ({
  day,
  timeTracked,
  projects,
  costCenters,
  disabled,
  onDataChange,
  onDelete,
}) => {
  const { id: meId } = useUserInfo();

  const availableProjects = projects
    .filter((project) =>
      isProjectAvailable(project, timeTracked.projectId || null),
    )
    .map(mapToOptions);

  const availableCostCenters = [{ Id: '', Name: '' }, ...costCenters];

  const handleHoursChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        onDataChange({ ...timeTracked, hours: null });
        return;
      }
      const [hoursStr, minutesStr] = event.target.value.split(':');
      const totalMinutes =
        parseInt(hoursStr, 10) * 60 + parseInt(minutesStr, 10);
      onDataChange({
        ...timeTracked,
        hours: minToHoursInput(totalMinutes),
      });
    },
    [onDataChange, timeTracked],
  );

  const handleProjectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      onDataChange({ ...timeTracked, projectId: event.target.value });
    },
    [onDataChange, timeTracked],
  );

  const handleCostCenterChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      onDataChange({ ...timeTracked, costCenterId: event.target.value });
    },
    [onDataChange, timeTracked],
  );

  const handleNotesChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onDataChange({ ...timeTracked, notes: event.target.value });
    },
    [onDataChange, timeTracked],
  );

  const handleExtraFieldChange = useCallback(
    (
      extraFieldsInputs: ExtraFieldInput[],
      recordTypeName: string | undefined,
    ) => {
      let updatedObject = {
        ...timeTracked,
        extraFields: extraFieldsInputs,
      };
      if (recordTypeName !== undefined) {
        updatedObject = {
          ...updatedObject,
          recordTypeName: recordTypeName,
        };
      }
      onDataChange(updatedObject);
    },
    [onDataChange, timeTracked],
  );

  const formatHoursToHHMM = (hours: number | undefined | null): string => {
    if (hours === undefined || hours === null || hours === 0) {
      return '';
    }
    const totalMinutes = Math.round(hoursToMinutes(hours));
    const [h, m] = minutesToHoursAndMinutes(totalMinutes);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Row>
        <Col lg={3} xs={4}>
          <FormGroup>
            <StyledSelect
              disabled={disabled}
              required
              value={timeTracked.projectId || undefined}
              onChange={handleProjectChange}>
              {availableProjects.map((availableProject) => (
                <option
                  key={availableProject.value}
                  value={availableProject.value}>
                  {availableProject.label}
                </option>
              ))}
            </StyledSelect>
          </FormGroup>
        </Col>
        <Col lg={2} xs={2}>
          <Form.Control
            value={
              timeTracked.hours === 0
                ? '00:00'
                : formatHoursToHHMM(timeTracked.hours)
            }
            type="time"
            required
            disabled={disabled}
            onChange={handleHoursChange}
          />
        </Col>
        <Col lg={4} xs={5}>
          <Form.Control
            as="textarea"
            rows={1}
            disabled={disabled}
            onChange={handleNotesChange}
            value={timeTracked.notes}
          />
        </Col>
        {costCenters.length > 0 && (
          <Col lg={2} xs={3}>
            <FormGroup>
              <StyledSelect
                disabled={disabled}
                value={timeTracked.costCenterId || undefined}
                onChange={handleCostCenterChange}>
                {availableCostCenters.map((costCenter) => (
                  <option key={costCenter.Id} value={costCenter.Id}>
                    {costCenter.Name}
                  </option>
                ))}
              </StyledSelect>
            </FormGroup>
          </Col>
        )}
        {!disabled && (
          <Col lg={1} xs={1}>
            <Button
              variant="link"
              disabled={disabled}
              onClick={() => onDelete(timeTracked.id)}>
              <FlairIcon icon="trash-outline" color={Theme.color.gray1} />
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col lg={8} xs={12}>
          <ExtraFieldsAreaSimple
            objectApiName={ExtraFieldsSupportedObject.ProjectTimeEntry}
            formId={ExtraFieldsFormId.ProjectsTimeEntryEdit}
            onChange={handleExtraFieldChange}
            values={timeTracked.extraFields}
            recordTypeName={timeTracked.recordTypeName}
            contextParams={[
              {
                name: 'day',
                type: FieldType.Date,
                value: day,
              },
              {
                name: 'project',
                type: FieldType.Reference,
                value: timeTracked.projectId ?? '',
              },
              {
                name: 'employee',
                type: FieldType.Reference,
                value: meId,
              },
            ]}
            disabled={disabled}
          />
        </Col>
      </Row>
    </>
  );
};

export default DayTimeEntry;

function mapToOptions(project: ProjectWithStatus): OptionBase {
  return {
    value: project.id,
    label: project.name,
  };
}

function isProjectAvailable(
  project: ProjectWithStatus,
  selectedProjectId: string | null,
) {
  return selectedProjectId === project.id || project.status === 'ACTIVE';
}
